import PropTypes from 'prop-types';
import { FireIcon, CubeIcon } from '@heroicons/react/24/outline';
import config from '@/config';
import { MONTH_NAMES, moduleBadge, formatCo2e } from '@/utils/gconUtils';

/** Smallest fraction of the shared axis a source may occupy, so the very
 *  smallest emitters still render something readable and hoverable. */
const MIN_STRIP_EXTENT = 0.06;

/**
 * One dot per month on a CO₂e axis shared by every row, so strips are directly
 * comparable: the axis only extends as far as that source's peak month, making
 * its length a read on magnitude while dot spread reads as variability.
 *
 * Sources too small to resolve on the shared scale fall back to their own scale
 * within `MIN_STRIP_EXTENT` — the short axis still signals "small source".
 */
const DotStrip = ({ monthlyData, globalMax }) => {
  if (!monthlyData?.length) return null;
  const values = monthlyData.map(d => d.co2e);
  const srcMax = Math.max(...values, 0);
  const mean = values.reduce((a, b) => a + b, 0) / values.length;

  const extent = globalMax > 0 ? srcMax / globalMax : 0;
  const shown = Math.max(extent, MIN_STRIP_EXTENT);
  const clamped = extent < MIN_STRIP_EXTENT;
  // value → fraction of the container. Collapses to v/globalMax when unclamped.
  const scale = srcMax > 0 ? shown / srcMax : 0;
  const pos = v => v * scale * 100;

  return (
    <div className="h-8 w-full px-1.5">
      <div className="relative h-full w-full">
        {/* Axis — length encodes this source's peak month */}
        <div
          className="absolute top-1/2 h-px bg-gray-200"
          style={{ left: 0, width: `${shown * 100}%` }}
          title={`Peak ${srcMax.toFixed(1)} t CO₂e${clamped ? ' · own scale (too small for shared axis)' : ' · shared scale'}`}
        />
        {/* End cap */}
        <div
          className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-px h-2 bg-gray-300"
          style={{ left: `${shown * 100}%` }}
        />

        {/* Mean marker */}
        <div
          className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-px h-3.5 bg-gray-300"
          style={{ left: `${pos(mean)}%` }}
          title={`Mean: ${mean.toFixed(1)} t CO₂e`}
        />

        {/* Monthly observations */}
        {monthlyData.map((d, i) => (
          <div
            key={i}
            title={`${MONTH_NAMES[(d.month - 1) % 12]} ${d.year}: ${d.co2e.toFixed(1)} t CO₂e`}
            className={`absolute top-1/2 w-2 h-2 -translate-x-1/2 -translate-y-1/2 rounded-full
                       opacity-50 hover:opacity-100 hover:ring-2 hover:ring-teal-200
                       transition-opacity cursor-default ${clamped ? 'bg-gray-400' : 'bg-teal-500'}`}
            style={{ left: `${pos(d.co2e)}%` }}
          />
        ))}
      </div>
    </div>
  );
};

const takeIntoModelingUrl = spot => {
  const params = new URLSearchParams({ from: 'gcon', source: spot.source_name });
  if (spot.module) params.set('module', spot.module);
  if (spot.location) params.set('location', spot.location);
  if (spot.sub_location) params.set('sub_location', spot.sub_location);
  if (spot.scope) params.set('scope', spot.scope);
  params.set('co2e_t', String(Math.round(spot.total_co2e)));
  return `${config.gconModelingUrl}?${params.toString()}`;
};

const HotSpotRow = ({ spot, rank, globalMax, globalMonthlyMax }) => {
  const barPct = globalMax > 0 ? (spot.total_co2e / globalMax) * 100 : 0;
  const isTopEmitter = spot.emission_rank <= 3;

  return (
    <div className="relative bg-white border border-gray-100 rounded-lg p-4 hover:border-gray-300 transition-colors">
      {/* Hand off to pathway modelling — overlaid so the chart below runs full width.
          Only offered where a target pathway is configured for this deployment. */}
      {config.gconModelingUrl && (
        <button
          type="button"
          title="Open a Sesame pathway for this equipment"
          aria-label={`Take ${spot.source_name} into modeling`}
          onClick={() => window.open(takeIntoModelingUrl(spot), '_blank', 'noopener,noreferrer')}
          className="btn btn-xs btn-outline gap-1.5 absolute top-4 right-4 whitespace-nowrap"
        >
          <CubeIcon className="w-3.5 h-3.5" />
          Take into modeling
        </button>
      )}

      <div className="flex items-start gap-3">
        {/* Rank */}
        <div
          className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold
          ${rank === 1 ? 'bg-orange-100 text-orange-700' : rank === 2 ? 'bg-gray-100 text-gray-600' : rank === 3 ? 'bg-amber-50 text-amber-700' : 'bg-gray-50 text-gray-500'}`}
        >
          {rank}
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* pr-44 clears the overlaid button; rows below it run full width */}
          <div className={`flex items-center gap-2 flex-wrap mb-1 ${config.gconModelingUrl ? 'pr-44' : ''}`}>
            <span className="text-sm font-semibold text-gray-800 truncate">{spot.source_name}</span>
            {isTopEmitter && <FireIcon className="w-4 h-4 text-orange-500 flex-shrink-0" title="Top emitter" />}
          </div>

          <div className={`flex items-center gap-2 mb-2 flex-wrap ${config.gconModelingUrl ? 'pr-44' : ''}`}>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${moduleBadge(spot.module)}`}>
              {spot.module || 'Unknown'}
            </span>
            {spot.location && (
              <span className="text-xs text-gray-400">
                {spot.location}
                {spot.sub_location ? ` / ${spot.sub_location}` : ''}
              </span>
            )}
            <span className="text-xs text-gray-400">{spot.scope}</span>
          </div>

          {/* Emissions bar */}
          <div className="flex items-center gap-3 mb-2">
            <div className="flex-1 bg-gray-100 rounded-full h-1.5">
              <div className="bg-teal-500 h-1.5 rounded-full" style={{ width: `${barPct}%` }} />
            </div>
            <span className="text-xs font-semibold text-gray-700 w-20 text-right">
              {formatCo2e(spot.total_co2e)} CO₂e
            </span>
          </div>

          {/* Monthly distribution + stats */}
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <DotStrip monthlyData={spot.monthly_data} globalMax={globalMonthlyMax} />
            </div>
            <div className="flex gap-4 text-right">
              <div>
                <p className="text-xs text-gray-400">Avg/month</p>
                <p className="text-xs font-medium text-gray-700">{formatCo2e(spot.avg_monthly_co2e)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

DotStrip.propTypes = {
  monthlyData: PropTypes.arrayOf(
    PropTypes.shape({ year: PropTypes.number, month: PropTypes.number, co2e: PropTypes.number }),
  ),
  globalMax: PropTypes.number.isRequired,
};

HotSpotRow.propTypes = {
  spot: PropTypes.object.isRequired,
  rank: PropTypes.number.isRequired,
  globalMax: PropTypes.number.isRequired,
  globalMonthlyMax: PropTypes.number.isRequired,
};

export default HotSpotRow;
